import { FieldNames } from '@/constants/FieldNames';
import ICompAddress, { newCompAddress } from '../CompAddress';
import IAddress1UnitNoZipCode from '../common/IAddress1UnitNoZipCode';

export default {
    copy(source: ICompAddress, target: ICompAddress): void {
        target.address1 = source.address1;
        target.address2 = source.address2;
        target.unitNumber = source.unitNumber;
        target.city = source.city;
        target.state = source.state;
        target.zipCode = source.zipCode;
    },

    /**
     * Combines the address1 and unit number from the given comp address. Includes a
     * standard separator when unit number is truthy. The separator is FieldNames.UnitNo.
     * @param compAddress
     * @returns
     */
    combineAddress1AndUnitNo(compAddress: ICompAddress): string {
        const result = this.combineAddressAndUnitNo(compAddress.address1, compAddress.unitNumber);
        return result;
    },

    /**
     * Combines the given address with the given unit number, providing a standard separator when
     * unit number is truthy. The separator is FieldNames.UnitNo.
     * @param address
     * @param unitNumber
     * @returns
     */
    combineAddressAndUnitNo(address: string | null, unitNumber: string | null | undefined): string {
        let result = address ?? '';

        if (unitNumber) {
            result += ' ' + FieldNames.UnitNo + ' ' + unitNumber;
        }

        return result;
    },

    /**
     * Splits the given address1 and unit number string into component parts, returning
     * a comp address with the values. When unit number exists in the given string it is
     * expected to have the standard separator as that provided by the combine methods
     * found in this utils file.
     * @param address1AndUnitNumber
     * @returns
     */
    splitAddress1AndUnitNo(address1AndUnitNumber: string): ICompAddress {
        const result: ICompAddress = newCompAddress();
        if (address1AndUnitNumber.includes(FieldNames.UnitNo)) {
            const split = address1AndUnitNumber.split(FieldNames.UnitNo);
            result.address1 = split[0].trim();
            result.unitNumber = split[1].trim();
        } else {
            result.address1 = address1AndUnitNumber;
            result.unitNumber = '';
        }

        return result;
    },

    /**
     * Get an address1, unit number and zip code object from the given comp address.
     * @param compAddress
     * @returns
     */
    getAddress1UnitNoAndZipCode(compAddress: ICompAddress): IAddress1UnitNoZipCode {
        const result: IAddress1UnitNoZipCode = {
            address1: compAddress.address1 ? '' + compAddress.address1 : '',
            unitNumber: compAddress.unitNumber ? '' + compAddress.unitNumber : '',
            zipCode: compAddress.zipCode ? '' + compAddress.zipCode : ''
        };

        return result;
    },

    /**
     * Get a descriptive string for the given address.
     * @param compAddress
     * @returns
     */
    getDescription(compAddress: ICompAddress): string {
        let result = '';
        if (compAddress) {
            result = compAddress.address1;

            if (compAddress.unitNumber) {
                result += ' ' + compAddress.unitNumber;
            }

            if (compAddress.city && compAddress.state && compAddress.zipCode) {
                result += ' ' + compAddress.city;
                result += ', ' + compAddress.state;
                result += ' ' + compAddress.zipCode;
            } else if (compAddress.address2) {
                result += ' ' + compAddress.address2;
            }
        } else {
            result = 'No comp address was provided';
        }
        return result;
    },

    /**
     * Get a short descriptive string for the given address.
     * Includes address1, unit number and zip code.
     * @param compAddress
     * @returns
     */
    getShortDescription(compAddress: ICompAddress): string {
        let result = '';
        if (compAddress) {
            result = compAddress.address1;

            if (compAddress.unitNumber) {
                result += ' ' + compAddress.unitNumber;
            }
            if (compAddress.zipCode) {
                result += ', ' + compAddress.zipCode;
            }
        }
        return result;
    }
};
